/*
 * aura-spotlight.js: the home customer spotlight.
 * Once the section is near the screen, fetches the Explore backend's /ugc feed (the latest Instagram
 * posts that tag @auravinylwrap) and fills the server-rendered square tiles, each a link to its post.
 * Only https Instagram post links and https images are used; tiles show each image's 300px square
 * (-300.jpg) and fall back to the full image if that is missing. If the feed fails or is empty, the
 * tiles are hidden and the Instagram link stays.
 */
(() => {
  const track = (n, p) => window.aura && window.aura.track(n, p);
  const valid = (post) => post && /^https:\/\/(www\.)?instagram\.com\//.test(post.url) && /^https:\/\//.test(post.img);
  const small = (src) => src.replace(/\.jpe?g$/i, '-300.jpg');

  class AuraSpotlight extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-spotlight-grid]');
      if (!this.grid || this.observer) return;
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      }, { rootMargin: '600px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() { this.observer?.disconnect(); }

    async load() {
      let posts;
      try {
        const res = await fetch(this.dataset.feed, { credentials: 'omit' });
        if (!res.ok) throw new Error(`ugc ${res.status}`);
        posts = await res.json();
      } catch (e) { return this.fail(); }

      const tiles = [...this.grid.children];
      posts = (Array.isArray(posts) ? posts : []).filter(valid).slice(0, tiles.length);
      if (!posts.length) return this.fail();

      tiles.forEach((tile, i) => {
        const post = posts[i];
        if (!post) { tile.remove(); return; }
        const link = document.createElement('a');
        link.href = post.url;
        link.target = '_blank';
        link.rel = 'noopener';
        link.addEventListener('click', () => track('aura_home_spotlight_post', { position: i + 1 }));
        const img = new Image(300, 300);
        img.alt = tile.dataset.label || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
        img.addEventListener('error', () => { img.removeAttribute('srcset'); img.src = post.img; }, { once: true });
        if (window.auraImg && window.auraImg.can(post.img)) {
          img.sizes = `${Math.round(tile.getBoundingClientRect().width) || 120}px`;
          img.srcset = window.auraImg.srcset(post.img, [120, 240, 360, 480]);
          img.src = window.auraImg.src(post.img, 360);
        } else {
          img.src = small(post.img);
        }
        link.append(img);
        tile.append(link);
      });
      this.grid.removeAttribute('aria-busy');
    }

    fail() {
      this.grid.hidden = true;
      this.grid.removeAttribute('aria-busy');
    }
  }

  if (!customElements.get('aura-spotlight')) customElements.define('aura-spotlight', AuraSpotlight);
})();
